import { useEffect, useState } from 'react'
import { useMediaQuery } from '@mui/material'
import { WarningIcon } from '@phosphor-icons/react'

import { BaseModal2 } from '@components/modals/BaseModal2'
import { currencyMask } from '@utils/masks/currencyMask'
import { emotionTheme } from '@styles/theme'
import { getPagePermissions } from '@hooks/useUser/provider'
import { initialPagination, type PaginationParams } from '@components/shared/Table/components/Pagination/types'
import { OcNfPurchaseOrdersFilters } from './components/OcNfPurchaseOrdersFilters'
import { OcNfStatus } from './components/OcNfStatus'
import type { StatusProps } from './components/OcNfStatus/types'
import { PageIds } from '@hooks/useLayout/types'
import { parseOcNfDetailsGrid } from './helpers/parseOcNfDetailsGrid'
import { parseOcNfItemsGrid } from './helpers/parseOcNfItemsGrid'
import { PurchaseOrderDetails } from './components/PurchaseOrderDetails'
import { ReturnModal } from '@components/modals/ReturnModal'
import { RowTableTotal2 } from '@components/shared/RowTableTotal2'
import { scrollToTop } from './helpers/scrollToTop'
import { Table } from '@components/shared/Table'
import { Title } from '@components/shared/Title'
import { useOcNfModal } from './context'
import { useUser } from '@hooks/useUser'

import type { OcNfPurchaseOrdersFiltersFormProps } from './components/OcNfPurchaseOrdersFilters/form/types'
import type { PermissionsItemProps } from '@pages/AccessProfile/context/types'
import type { OcNfModalPurchaseOrdersProps } from './context/types'
import type { SortProps } from '@components/shared/Table/types'
import type { ClientModalProps } from './types'

export const OcNfModal = ({ data, open, onClose, canEdit = true, ...rest }: ClientModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const { filiData, userPermissionsData } = useUser()
  const {
    purchaseOrdersData,
    isPurchaseOrdersLoading,
    setPurchaseOrdersData,
    getPurchaseOrders,
    getItemsPurchaseOrders,
    itemsPurchaseOrdersData,
    isItemsPurchaseOrdersLoading,
    isReversePurchaseOrderLoading,
    isTransferPurchaseOrderLoading,
    reversePurchaseOrder,
    transferPurchaseOrder
  } = useOcNfModal()

  const [purchaseOrdersFormData, setPurchaseOrdersFormData] = useState<OcNfPurchaseOrdersFiltersFormProps | null>(null)
  const [purchaseOrdersItemData, setPurchaseOrdersItemData] = useState<OcNfModalPurchaseOrdersProps | null>(null)
  const [openTransferOcModal, setOpenTransferOcModal] = useState(false)
  const [openRevertOcModal, setOpenRevertOcModal] = useState(false)
  const [permissions, setPermissions] = useState<PermissionsItemProps | null>(null)
  const canEditPage = canEdit && permissions?.canEdit === true

  const handleResetValues = () => {
    setPurchaseOrdersFormData(null)
    setPurchaseOrdersItemData(null)
    setPurchaseOrdersData(null)
  }

  const handleGetItemsPurchaseOrders = (
    itemData: OcNfModalPurchaseOrdersProps | null,
    params?: PaginationParams | null,
    sortData?: SortProps
  ) => {
    getItemsPurchaseOrders({
      ...sortData,
      purchaseOrderNumber: itemData?.purchaseOrderNumber!,
      filiCode: filiData?.filiCode!,
      competenceDate: filiData?.startPeriod!,
      supplierCode: data?.supplier.code!,
      page: params?.page || initialPagination.PAGE,
      itemsPerPage: params?.itemsPerPage || itemsPurchaseOrdersData?.itemsPerPage || initialPagination.ITEMS_PER_PAGE
    })
    setPurchaseOrdersItemData(itemData)
  }

  const handleGetPurchaseOrders = (
    params: PaginationParams | null,
    formData?: OcNfPurchaseOrdersFiltersFormProps | null,
    sortData?: SortProps
  ) => {
    getPurchaseOrders({
      ...sortData,
      ...(formData || purchaseOrdersFormData),
      filiCode: filiData?.filiCode!,
      competenceDate: filiData?.startPeriod!,
      supplierCode: data?.supplier.code!,
      itemClassification: data?.itemClassification!,
      page: params?.page || initialPagination.PAGE,
      itemsPerPage: params?.itemsPerPage || purchaseOrdersData?.itemsPerPage || initialPagination.ITEMS_PER_PAGE
    })
    !!formData && setPurchaseOrdersFormData(formData)
  }

  const handleOcUpdatedData = () => {
    if (!!itemsPurchaseOrdersData && !!purchaseOrdersItemData) {
      const ocUpdatedData = purchaseOrdersData?.purchaseOrders?.find(
        (item) => item.purchaseOrderNumber === purchaseOrdersItemData?.purchaseOrderNumber
      )

      if (!!ocUpdatedData) {
        setPurchaseOrdersItemData(ocUpdatedData)
        handleGetItemsPurchaseOrders(ocUpdatedData)
      }
    }
  }

  const handleInitialActionsFromScreen = () => {
    handleGetPurchaseOrders(null)
  }

  useEffect(() => {
    !!filiData?.filiCode && !!open && !!data && handleInitialActionsFromScreen()
    !open && handleResetValues()
  }, [open, data, filiData])

  useEffect(() => {
    !!purchaseOrdersItemData && scrollToTop()
  }, [purchaseOrdersItemData])

  useEffect(() => {
    handleOcUpdatedData()
  }, [purchaseOrdersData])

  useEffect(() => {
    const pagePermissions = getPagePermissions(PageIds.CONSUMPTION)
    setPermissions(pagePermissions)
  }, [userPermissionsData])

  return (
    <>
      <BaseModal2
        open={open}
        width={isMobile ? 'calc(100% - 20px)' : '96.5%'}
        heightContainer="auto"
        maxHeightContainer={isMobile ? 'calc(100% - 20px)' : '95%'}
        headerIcon={`Fornecedor ${data?.supplier.code} - ${data?.supplier.name}`}
        onClose={onClose}
        footer={{
          cancelButtonText: 'Voltar',
          cancelButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5
        }}
        {...rest}
      >
        {!!purchaseOrdersItemData && (
          <>
            <Title
              label={`Detalhes OC (Ordem de Compra): ${purchaseOrdersItemData?.purchaseOrderNumber || ''}`}
              action={
                purchaseOrdersItemData?.status ? (
                  <OcNfStatus status={purchaseOrdersItemData?.status as StatusProps} />
                ) : null
              }
              marginTop={0}
              marginBottom={32}
            />

            <PurchaseOrderDetails
              mainData={data}
              data={purchaseOrdersItemData}
              supplierCode={data?.supplier.code!}
              purchaseOrderNumber={purchaseOrdersItemData?.purchaseOrderNumber!}
              canEdit={canEditPage}
            />

            <Title label="Lista de Itens" marginTop={40} marginBottom={30} />

            <Table
              data={parseOcNfItemsGrid({
                data: itemsPurchaseOrdersData?.items || []
              })}
              fixedRow={{
                element: (
                  <RowTableTotal2
                    data={[{ label: 'Total Registros', value: itemsPurchaseOrdersData?.totalItens || 0 }]}
                  />
                )
              }}
              footer={{
                pagination: {
                  currentPage: itemsPurchaseOrdersData?.currentPage || initialPagination.PAGE,
                  itemsPerPage: itemsPurchaseOrdersData?.itemsPerPage || initialPagination.ITEMS_PER_PAGE,
                  totalItems: itemsPurchaseOrdersData?.totalItens || 0,
                  totalPages: itemsPurchaseOrdersData?.totalPages || 0,
                  onPageChange: (page) => handleGetItemsPurchaseOrders(purchaseOrdersItemData, { page }),
                  onItemsPerPageChange: (itemsPerPage) =>
                    handleGetItemsPurchaseOrders(purchaseOrdersItemData, { itemsPerPage })
                }
              }}
              onSort={(sortData) => handleGetItemsPurchaseOrders(purchaseOrdersItemData, null, sortData)}
              isLoading={isItemsPurchaseOrdersLoading}
            />

            {/* {canEditPage && (
              <S.Buttons>
                <Tooltip
                  width={isMobile ? '100%' : 'fit-content'}
                  title={
                    purchaseOrdersItemData?.status === OcNfStatusEnum.CURRENT
                      ? 'Só é possível reverter uma OC com status Vigente e sem NF emitida'
                      : ''
                  }
                  placement="top"
                >
                  <Button
                    width={isMobile ? '100%' : 165}
                    colorType="outlineBlue"
                    onClick={() => setOpenRevertOcModal(true)}
                    disabled={
                      purchaseOrdersItemData?.status === OcNfStatusEnum.CURRENT ||
                      !!purchaseOrdersItemData?.isRevertBlocked
                    }
                  >
                    Reverter OC
                  </Button>
                </Tooltip>

                <Tooltip
                  width={isMobile ? '100%' : 'fit-content'}
                  title={
                    !!purchaseOrdersItemData?.invoiceNumber
                      ? 'Não é possível transferir uma OC com NF emitida'
                      : purchaseOrdersItemData?.status === OcNfStatusEnum.TRANSFER
                        ? 'Não é possível transferir uma OC já com status Transferida'
                        : ''
                  }
                  placement="top"
                >
                  <Button
                    width={isMobile ? '100%' : 165}
                    colorType="solidBlue"
                    onClick={() => setOpenTransferOcModal(true)}
                    disabled={
                      purchaseOrdersItemData?.status === OcNfStatusEnum.TRANSFER ||
                      !!purchaseOrdersItemData?.invoiceNumber ||
                      !!purchaseOrdersItemData?.isTransferBlocked
                    }
                  >
                    Transferir OC
                  </Button>
                </Tooltip>
              </S.Buttons>
            )} */}
          </>
        )}

        <Title label="Ordens de Compra" marginTop={!!purchaseOrdersItemData ? 40 : 0} marginBottom={8} />

        <OcNfPurchaseOrdersFilters onFilter={(formData) => handleGetPurchaseOrders(null, formData)} />

        <Table
          data={parseOcNfDetailsGrid({
            data: purchaseOrdersData?.purchaseOrders || [],
            onViewMore: handleGetItemsPurchaseOrders
          })}
          fixedRow={{
            element: (
              <RowTableTotal2
                data={[
                  { label: 'Total Registros', value: purchaseOrdersData?.totalItens || 0 },
                  {
                    label: 'Total OC',
                    value: currencyMask(purchaseOrdersData?.purchaseOrderTotalAmount?.toFixed(2) || 0)
                  }
                  // { label: 'Total NF', value: currencyMask(purchaseOrdersData?.invoiceTotalAmount?.toFixed(2) || 0) },
                  // { label: 'Total Desvio', value: currencyMask(purchaseOrdersData?.deviationTotalAmount?.toFixed(2) || 0) },
                  // { label: 'Total Transferido', value: currencyMask(purchaseOrdersData?.expectTotalAmount?.toFixed(2) || 0) }
                ]}
              />
            )
          }}
          footer={{
            pagination: {
              currentPage: purchaseOrdersData?.currentPage || initialPagination.PAGE,
              itemsPerPage: purchaseOrdersData?.itemsPerPage || initialPagination.ITEMS_PER_PAGE,
              totalItems: purchaseOrdersData?.totalItens || 0,
              totalPages: purchaseOrdersData?.totalPages || 0,
              onPageChange: (page) => handleGetPurchaseOrders({ page }),
              onItemsPerPageChange: (itemsPerPage) => handleGetPurchaseOrders({ itemsPerPage })
            }
          }}
          onSort={(sortData) => handleGetPurchaseOrders(null, null, sortData)}
          isLoading={isPurchaseOrdersLoading}
        />
      </BaseModal2>

      <ReturnModal
        open={openTransferOcModal}
        headerIcon={<WarningIcon weight="bold" />}
        title="Tem certeza de que deseja transferir a OC para a próxima Competência?"
        headerTitle="Transferir Ordem de Compra"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenTransferOcModal}
        onConfirm={() => {
          if (!canEditPage) return

          transferPurchaseOrder({
            filiCode: filiData?.filiCode!,
            competenceDate: filiData?.startPeriod!,
            supplierCode: data?.supplier.code!,
            purchaseOrderNumber: purchaseOrdersItemData?.purchaseOrderNumber!,
            itemClassification: data?.itemClassification!
          }).then((isSuccess) => isSuccess && setOpenTransferOcModal(false))
        }}
        isLoading={isTransferPurchaseOrderLoading}
      />

      <ReturnModal
        open={openRevertOcModal}
        headerIcon={<WarningIcon weight="bold" />}
        title="Tem certeza de que deseja reverter a OC?"
        headerTitle="Reverter Ordem de Compra"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenRevertOcModal}
        onConfirm={() => {
          reversePurchaseOrder({
            filiCode: filiData?.filiCode!,
            competenceDate: filiData?.startPeriod!,
            supplierCode: data?.supplier.code!,
            purchaseOrderNumber: purchaseOrdersItemData?.purchaseOrderNumber!,
            itemClassification: data?.itemClassification!
          }).then((isSuccess) => isSuccess && setOpenRevertOcModal(false))
        }}
        isLoading={isReversePurchaseOrderLoading}
      />
    </>
  )
}
